import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/app/hooks';
import { selectMeasurementUnit } from '@/features/preferences/preferencesSlice';
import { convertInchesToUnit, unitPrecision } from '@/utils/measurement';
import { calculateCurrentDpi } from './utils/printSizeForm';

interface ImageZoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  widthIn?: number | null;
  heightIn?: number | null;
  marginIn?: number | null;
  galleryWrapIn?: number | null;
  originalWidthPx?: number | null;
  originalHeightPx?: number | null;
  downloadUrl?: string | null;
  downloadLabel?: string | null;
  showDownload?: boolean;
}

export const ImageZoomModal: React.FC<ImageZoomModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  widthIn,
  heightIn,
  marginIn,
  galleryWrapIn,
  originalWidthPx,
  originalHeightPx,
  downloadUrl,
  downloadLabel,
  showDownload = true,
}) => {
  if (!isOpen) return null;

  const { t } = useTranslation();
  const measurementUnit = useAppSelector(selectMeasurementUnit);
  const precision = useMemo(() => unitPrecision(measurementUnit), [measurementUnit]);

  const formatDimension = (value?: number | null): string | null => {
    if (typeof value !== 'number' || value <= 0) {
      return null;
    }
    const converted = convertInchesToUnit(value, measurementUnit);
    return `${converted.toFixed(precision)} ${measurementUnit}`;
  };

  const { dpiX, dpiY } = calculateCurrentDpi({
    pixelWidth: originalWidthPx ?? null,
    pixelHeight: originalHeightPx ?? null,
    widthIn: widthIn ?? null,
    heightIn: heightIn ?? null,
    marginIn: marginIn ?? null,
  });

  const formattedWidth = formatDimension(widthIn);
  const formattedHeight = formatDimension(heightIn);
  const sizeLabel = formattedWidth && formattedHeight ? `${formattedWidth} × ${formattedHeight}` : '—';

  const formattedMargin = formatDimension(marginIn);
  const formattedGalleryWrap = formatDimension(galleryWrapIn);
  const edgeTreatmentLabel = formattedMargin
    ? t('orderSummary.marginValue', 'Margin')
    : formattedGalleryWrap
      ? t('orderSummary.galleryWrapValue', 'Gallery Wrap')
      : t('orderSummary.marginGalleryWrapValue', 'Margin / Gallery Wrap');
  const edgeTreatmentValue = formattedMargin ?? formattedGalleryWrap ?? '—';
  const dpiLabel = dpiX && dpiY ? `${dpiX} × ${dpiY}` : '—';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl bg-background p-3 shadow-lg sm:p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-center rounded-xl bg-black/80 p-2">
            <img src={imageUrl} alt="Zoomed image" className="max-h-[65vh] w-full object-contain" />
          </div>
          <div className="rounded-xl border border-border bg-card p-3 text-sm text-card-foreground sm:p-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('orderSummary.printSize', 'Print Size')}</p>
                <p className="mt-1 font-medium">{sizeLabel}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{edgeTreatmentLabel}</p>
                <p className="mt-1 font-medium">{edgeTreatmentValue}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('orderSummary.printDpi', 'PPI (print size)')}</p>
                <p className="mt-1 font-medium">{dpiLabel}</p>
              </div>
            </div>
          </div>
          {showDownload && downloadUrl && downloadLabel ? (
            <div className="flex justify-center">
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition hover:bg-primary/90"
              >
                {downloadLabel}
              </a>
            </div>
          ) : null}
        </div>
        <button
          onClick={onClose}
          className="absolute top-2 right-2 rounded-full bg-black/60 p-2 text-white"
        >
          &times;
        </button>
      </div>
    </div>
  );
};
